import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  ArrowLeft,
  Building2,
  MapPin,
  FileText,
  Users,
  TrendingUp,
  Sparkles,
  Plus,
  Calendar as CalendarIcon,
  AlertCircle,
  Check,
  Clock,
  XCircle,
  Loader2,
  Edit,
  Upload,
  Download,
  Trash2
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";

const AssetDetailPage = () => {
  const { assetId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [appraisalLoading, setAppraisalLoading] = useState(false);
  const [appraisalResult, setAppraisalResult] = useState(null);
  
  // Modals
  const [showDocModal, setShowDocModal] = useState(false);
  const [showLeaseModal, setShowLeaseModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showEditDocModal, setShowEditDocModal] = useState(false);
  const [showEditLeaseModal, setShowEditLeaseModal] = useState(false);
  const [editDocForm, setEditDocForm] = useState({ doc_id: "", doc_type: "", status: "", notes: "", file: null });
  const [editLeaseForm, setEditLeaseForm] = useState({ lease_id: "", tenant_name: "", tenant_email: "", tenant_phone: "", start_date: null, end_date: null, rent_amount: "", payment_frequency: "monthly", status: "active" });
  
  // Form states
  const [docForm, setDocForm] = useState({
    doc_type: "",
    status: "missing",
    notes: "",
    file: null
  });
  const [uploadingFile, setUploadingFile] = useState(false);
  
  const [leaseForm, setLeaseForm] = useState({
    tenant_name: "",
    tenant_email: "",
    tenant_phone: "",
    start_date: null,
    end_date: null,
    rent_amount: "",
    payment_frequency: "monthly"
  });

  const [editForm, setEditForm] = useState({});
  const [downloadingReport, setDownloadingReport] = useState(false);

  const fetchAsset = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/assets/${assetId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAsset(response.data);
      setEditForm({
        name: response.data.name,
        asset_type: response.data.asset_type || "",
        state: response.data.state || "",
        lga: response.data.lga || "",
        address: response.data.address || "",
        ownership_type: response.data.ownership_type || "",
        estimated_value: response.data.estimated_value || "",
        description: response.data.description || "",
        plot_number: response.data.plot_number || "",
        unit_number: response.data.unit_number || "",
        block_number: response.data.block_number || ""
      });
    } catch (error) {
      toast.error("Failed to load asset");
      navigate("/assets");
    } finally {
      setLoading(false);
    }
  }, [assetId, token, navigate]);

  useEffect(() => {
    fetchAsset();
  }, [fetchAsset]);

  const handleAddDocument = async () => {    let fileId = null;
    
    // Upload file if selected
    if (docForm.file) {
      setUploadingFile(true);
      try {
        const formData = new FormData();
        formData.append("file", docForm.file);
        const uploadRes = await axios.post(`${API}/files/upload?asset_id=${assetId}`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        });
        fileId = uploadRes.data.file_id;
      } catch (error) {
        toast.error("Failed to upload file");
        setUploadingFile(false);
        return;
      }
      setUploadingFile(false);
    }
    
    try {
      await axios.post(`${API}/documents`, {
        asset_id: assetId,
        doc_type: docForm.doc_type,
        status: docForm.status,
        notes: docForm.notes,
        file_id: fileId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Document added successfully");
      setShowDocModal(false);
      setDocForm({ doc_type: "", status: "missing", notes: "", file: null });
      fetchAsset();
    } catch (error) {
      toast.error("Failed to add document");
    }
  };

  const handleDownloadFile = async (fileId, filename) => {
    try {
      const response = await axios.get(`${API}/files/${fileId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename || "document");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download file");
    }
  };

  const handleDownloadAssetReport = async () => {
    setDownloadingReport(true);
    try {
      const response = await axios.get(`${API}/reports/asset/${assetId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      const safeName = asset?.name?.replace(/\s+/g, "_").slice(0, 30) || "Asset";
      link.setAttribute("download", `BlueElement_${safeName}_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Asset report downloaded");
    } catch (error) {
      toast.error("Failed to generate report");
    } finally {
      setDownloadingReport(false);
    }
  };

  const handleAddLease = async () => {
    try {
      await axios.post(`${API}/leases`, {
        asset_id: assetId,
        tenant_name: leaseForm.tenant_name,
        tenant_email: leaseForm.tenant_email,
        tenant_phone: leaseForm.tenant_phone,
        start_date: leaseForm.start_date?.toISOString(),
        end_date: leaseForm.end_date?.toISOString(),
        rent_amount: parseFloat(leaseForm.rent_amount),
        payment_frequency: leaseForm.payment_frequency
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Lease created successfully");
      setShowLeaseModal(false);
      setLeaseForm({
        tenant_name: "",
        tenant_email: "",
        tenant_phone: "",
        start_date: null,
        end_date: null,
        rent_amount: "",
        payment_frequency: "monthly"
      });
      fetchAsset();
    } catch (error) {
      toast.error("Failed to create lease");
    }
  };

  const openEditDoc = (doc) => {
    setEditDocForm({
      doc_id: doc.doc_id,
      doc_type: doc.doc_type || "",
      status: doc.status || "missing",
      notes: doc.notes || "",
      file: null
    });
    setShowEditDocModal(true);
  };

  const handleEditDocument = async () => {
    let fileId = undefined;
    if (editDocForm.file) {
      setUploadingFile(true);
      try {
        const formData = new FormData();
        formData.append("file", editDocForm.file);
        const uploadRes = await axios.post(`${API}/files/upload?asset_id=${assetId}`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
        });
        fileId = uploadRes.data.file_id;
      } catch {
        toast.error("Failed to upload file");
        setUploadingFile(false);
        return;
      }
      setUploadingFile(false);
    }
    try {
      const payload = { doc_type: editDocForm.doc_type, status: editDocForm.status, notes: editDocForm.notes };
      if (fileId) payload.file_id = fileId;
      await axios.put(`${API}/documents/${editDocForm.doc_id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Document updated");
      setShowEditDocModal(false);
      fetchAsset();
    } catch {
      toast.error("Failed to update document");
    }
  };

  const openEditLease = (lease) => {
    setEditLeaseForm({
      lease_id: lease.lease_id,
      tenant_name: lease.tenant_name || "",
      tenant_email: lease.tenant_email || "",
      tenant_phone: lease.tenant_phone || "",
      start_date: lease.start_date ? new Date(lease.start_date) : null,
      end_date: lease.end_date ? new Date(lease.end_date) : null,
      rent_amount: lease.rent_amount || "",
      payment_frequency: lease.payment_frequency || "monthly",
      status: lease.status || "active"
    });
    setShowEditLeaseModal(true);
  };

  const handleEditLease = async () => {
    try {
      await axios.put(`${API}/leases/${editLeaseForm.lease_id}`, {
        tenant_name: editLeaseForm.tenant_name,
        tenant_email: editLeaseForm.tenant_email,
        tenant_phone: editLeaseForm.tenant_phone,
        start_date: editLeaseForm.start_date?.toISOString(),
        end_date: editLeaseForm.end_date?.toISOString(),
        rent_amount: editLeaseForm.rent_amount ? parseFloat(editLeaseForm.rent_amount) : undefined,
        payment_frequency: editLeaseForm.payment_frequency,
        status: editLeaseForm.status
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Lease updated");
      setShowEditLeaseModal(false);
      fetchAsset();
    } catch {
      toast.error("Failed to update lease");
    }
  };

  const handleUpdateAsset = async () => {
    try {
      await axios.put(`${API}/assets/${assetId}`, {
        name: editForm.name,
        asset_type: editForm.asset_type || null,
        state: editForm.state || null,
        lga: editForm.lga || null,
        address: editForm.address || null,
        ownership_type: editForm.ownership_type || null,
        estimated_value: editForm.estimated_value ? parseFloat(editForm.estimated_value) : null,
        description: editForm.description,
        plot_number: editForm.plot_number || null,
        unit_number: editForm.unit_number || null,
        block_number: editForm.block_number || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Asset updated successfully");
      setShowEditModal(false);
      fetchAsset();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update asset");
    }
  };

  const handleDeleteAsset = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/assets/${assetId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Asset deleted successfully");
      navigate("/assets");
    } catch (error) {
      toast.error("Failed to delete asset");
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleGetAppraisal = async () => {
    if (user?.subscription_plan === "free") {
      toast.error("AI appraisal requires Professional or Enterprise plan");
      return;
    }
    
    setAppraisalLoading(true);
    try {
      const response = await axios.post(`${API}/appraisal`, {
        asset_id: assetId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAppraisalResult(response.data);
      toast.success("Appraisal generated successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate appraisal");
    } finally {
      setAppraisalLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₦0";
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'complete': return <Check className="w-4 h-4 text-emerald-500" />;
      case 'in_progress': return <Clock className="w-4 h-4 text-amber-500" />;
      case 'missing': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <AlertCircle className="w-4 h-4 text-slate-500" />;
    }
  };

  const documentTypes = [
    "Certificate of Occupancy (C of O)",
    "Deed of Assignment",
    "Survey Plan",
    "Governor's Consent",
    "Building Plan Approval",
    "Probate",
    "Letter of Administration",
    "Company Resolution",
    "Other"
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!asset) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <p className="text-slate-400">Asset not found</p>
        </div>
      </DashboardLayout>
    );
  }

  const isOwner = asset.user_id === user?.user_id;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="asset-detail-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <Link to="/assets">
            <Button variant="ghost" className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Assets
            </Button>
          </Link>
        </div>

        {/* Asset Overview */}
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card className="card-dashboard overflow-hidden">
              <div className="relative h-64">
                <img 
                  src={asset.image_url || "https://images.unsplash.com/photo-1757439402268-1da284675170?w=800"}
                  alt={asset.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="capitalize bg-primary/20 text-primary border-primary/30">
                      {asset.asset_type}
                    </Badge>
                    {!isOwner && (
                      <Badge className="bg-violet-500/20 text-violet-300 border-violet-500/30">
                        <Users className="w-3 h-3 mr-1" />
                        Team Asset
                      </Badge>
                    )}
                  </div>
                  <h1 className="text-3xl font-bold text-white">{asset.name}</h1>
                  <p className="text-slate-400 flex items-center gap-1 mt-1">
                    <MapPin className="w-4 h-4" />
                    {asset.address}, {asset.lga}, {asset.state}
                  </p>
                </div>
                {isOwner && (
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="absolute top-4 right-4 bg-slate-900/80 backdrop-blur-sm hover:bg-slate-800"
                    onClick={() => setShowEditModal(true)}
                    data-testid="edit-asset-btn"
                  >
                    <Edit className="w-4 h-4 text-white" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className={`absolute top-4 ${isOwner ? 'right-16' : 'right-4'} bg-slate-900/80 backdrop-blur-sm hover:bg-slate-800`}
                  onClick={handleDownloadAssetReport}
                  disabled={downloadingReport}
                  data-testid="download-asset-report-btn"
                >
                  {downloadingReport ? (
                    <Loader2 className="w-4 h-4 text-white animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 text-white" />
                  )}
                </Button>
                {isOwner && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-4 right-28 bg-red-900/80 backdrop-blur-sm hover:bg-red-800"
                    onClick={() => setShowDeleteDialog(true)}
                    data-testid="delete-asset-btn"
                  >
                    <Trash2 className="w-4 h-4 text-red-300" />
                  </Button>
                )}
              </div>
              
              <CardContent className="p-6">
                <div className="grid sm:grid-cols-3 gap-6">
                  <div>
                    <p className="text-slate-500 text-sm">Ownership</p>
                    <p className="text-white font-medium capitalize">{asset.ownership_type}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-sm">Estimated Value</p>
                    <p className="text-white font-medium mono">{formatCurrency(asset.estimated_value)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-sm">Status</p>
                    <div className="flex items-center gap-2">
                      <Badge className={asset.lease_status === 'leased' ? 'status-complete' : 'status-missing'}>
                        {asset.lease_status === 'leased' ? 'Leased' : 'Vacant'}
                      </Badge>
                    </div>
                  </div>
                </div>
                
                {/* Property Identifier */}
                {(asset.plot_number || asset.unit_number || asset.block_number) && (
                  <div className="mt-6 pt-6 border-t border-slate-800">
                    <div className="flex items-center gap-4">
                      {asset.plot_number && (
                        <div>
                          <p className="text-slate-500 text-sm">Plot / AGIS No.</p>
                          <p className="text-white font-mono font-medium">{asset.plot_number}</p>
                        </div>
                      )}
                      {asset.unit_number && (
                        <div>
                          <p className="text-slate-500 text-sm">Unit No.</p>
                          <p className="text-white font-mono font-medium">{asset.unit_number}</p>
                        </div>
                      )}
                      {asset.block_number && (
                        <div>
                          <p className="text-slate-500 text-sm">Block No.</p>
                          <p className="text-white font-mono font-medium">{asset.block_number}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {asset.description && (
                  <div className="mt-6 pt-6 border-t border-slate-800">
                    <p className="text-slate-500 text-sm mb-2">Description</p>
                    <p className="text-slate-300">{asset.description}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* AI Appraisal Card */}
          <div>
            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  AI Appraisal
                </CardTitle>
              </CardHeader>
              <CardContent>
                {user?.subscription_plan === "free" ? (
                  <div className="text-center py-4">
                    <p className="text-slate-400 text-sm mb-4">
                      Upgrade to Professional or Enterprise plan to unlock AI-powered property appraisals.
                    </p>
                    <Link to="/pricing">
                      <Button className="btn-primary" size="sm">
                        Upgrade Now
                      </Button>
                    </Link>
                  </div>
                ) : appraisalResult ? (
                  <div className="space-y-4">
                    <div className="p-4 rounded-xl bg-primary/10 border border-primary/30">
                      <p className="text-slate-400 text-sm mb-1">Estimated Value Range</p>
                      <p className="text-xl font-bold text-white mono">
                        {formatCurrency(appraisalResult.appraisal?.value_range?.min)} - {formatCurrency(appraisalResult.appraisal?.value_range?.max)}
                      </p>
                      <Badge className="mt-2 capitalize">
                        {appraisalResult.appraisal?.confidence} confidence
                      </Badge>
                    </div>
                    
                    {appraisalResult.appraisal?.factors?.length > 0 && (
                      <div>
                        <p className="text-slate-500 text-sm mb-2">Key Factors</p>
                        <ul className="space-y-1">
                          {appraisalResult.appraisal.factors.map((factor, idx) => (
                            <li key={`factor-${idx}-${factor.slice(0,20)}`} className="text-slate-300 text-sm flex items-center gap-2">
                              <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                              {factor}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    <p className="text-xs text-slate-500 italic">
                      {appraisalResult.disclaimer}
                    </p>
                    
                    <Button 
                      onClick={handleGetAppraisal}
                      className="w-full btn-secondary"
                      disabled={appraisalLoading}
                    >
                      Refresh Appraisal
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-slate-400 text-sm mb-4">
                      Get an AI-powered property valuation based on location, type, and market data.
                    </p>
                    <Button 
                      onClick={handleGetAppraisal}
                      className="btn-primary"
                      disabled={appraisalLoading}
                      data-testid="get-appraisal-btn"
                    >
                      {appraisalLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Get AI Appraisal
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Performance Card */}
            <Card className="card-dashboard mt-6">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                  Performance
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Active Leases</span>
                  <span className="text-white font-medium">
                    {asset.performance?.active_leases || 0} / {asset.performance?.total_leases || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Rental Income (collected)</span>
                  <span className="text-emerald-400 mono font-medium">
                    {formatCurrency(asset.performance?.rental_income || 0)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Outstanding Rent</span>
                  <span className="text-amber-400 mono font-medium" data-testid="asset-outstanding-rent">
                    {formatCurrency(asset.performance?.outstanding_rent || 0)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Maintenance Cost</span>
                  <span className="text-red-400 mono font-medium">
                    -{formatCurrency(asset.performance?.maintenance_cost || 0)}
                  </span>
                </div>
                <div className="flex items-center justify-between pt-4 border-t border-slate-800">
                  <span className="text-white font-medium">Net Performance</span>
                  <span className={`mono font-bold ${
                    (asset.performance?.net_performance || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'
                  }`}>
                    {formatCurrency(asset.performance?.net_performance || 0)}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Tabs for Documents and Leases */}
        <Tabs defaultValue="documents" className="w-full">
          <TabsList className="bg-slate-900/50 border border-slate-800">
            <TabsTrigger value="documents" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <FileText className="w-4 h-4 mr-2" />
              Documents
            </TabsTrigger>
            <TabsTrigger value="leases" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" />
              Leases
            </TabsTrigger>
          </TabsList>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card className="card-dashboard">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">Documentation</CardTitle>
                <Button 
                  onClick={() => setShowDocModal(true)}
                  className="btn-primary"
                  size="sm"
                  data-testid="add-document-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Document
                </Button>
              </CardHeader>
              <CardContent>
                {asset.documents?.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">No documents added yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {asset.documents?.map((doc, idx) => (
                      <div key={doc.doc_id || idx} className="flex items-center justify-between p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                        <div className="flex items-center gap-3">
                          {getStatusIcon(doc.status)}
                          <div>
                            <p className="text-white font-medium">{doc.doc_type}</p>
                            {doc.notes && <p className="text-slate-400 text-sm">{doc.notes}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {doc.file_id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-primary hover:text-primary/80"
                              onClick={() => handleDownloadFile(doc.file_id, doc.doc_type)}
                              data-testid={`download-doc-${idx}`}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              File
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-slate-400 hover:text-white"
                            onClick={() => openEditDoc(doc)}
                            data-testid={`edit-doc-${idx}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Badge className={
                            doc.status === 'complete' ? 'status-complete' :
                            doc.status === 'in_progress' ? 'status-pending' : 'status-missing'
                          }>
                            {doc.status.replace('_', ' ')}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Leases Tab */}
          <TabsContent value="leases">
            <Card className="card-dashboard">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">Lease History</CardTitle>
                <Button 
                  onClick={() => setShowLeaseModal(true)}
                  className="btn-primary"
                  size="sm"
                  data-testid="add-lease-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Lease
                </Button>
              </CardHeader>
              <CardContent>
                {asset.leases?.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">No leases recorded yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {asset.leases?.map((lease, idx) => (
                      <div key={lease.lease_id || idx} className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="text-white font-medium">{lease.tenant_name}</p>
                            <p className="text-slate-400 text-sm">{lease.tenant_email}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-slate-400 hover:text-white"
                              onClick={() => openEditLease(lease)}
                              data-testid={`edit-lease-${idx}`}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            <Badge className={lease.status === 'active' ? 'status-complete' : 'status-missing'}>
                              {lease.status}
                            </Badge>
                            <Badge className={lease.payment_status === 'paid' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' : 'bg-amber-500/10 text-amber-400 border-amber-500/30'} data-testid={`asset-lease-payment-${idx}`}>
                              {lease.payment_status === 'paid' ? 'Rent Paid' : 'Rent Pending'}
                            </Badge>
                            {!lease.has_agreement && (
                              <Link to="/leases" className="inline-flex" data-testid={`asset-lease-agreement-missing-${idx}`}>
                                <Badge className="bg-red-500/10 text-red-400 border-red-500/30 hover:bg-red-500/20">Agreement missing</Badge>
                              </Link>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-slate-500">Rent</p>
                            <p className="text-white mono">{formatCurrency(lease.rent_amount)}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Start Date</p>
                            <p className="text-white">{new Date(lease.start_date).toLocaleDateString()}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">End Date</p>
                            <p className="text-white">{new Date(lease.end_date).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Add Document Modal */}
        <Dialog open={showDocModal} onOpenChange={setShowDocModal}>
          <DialogContent className="bg-slate-900 border-slate-800">
            <DialogHeader>
              <DialogTitle className="text-white">Add Document</DialogTitle>
              <DialogDescription className="text-slate-400">
                Track documentation for this asset
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Document Type</Label>
                <Select 
                  value={docForm.doc_type} 
                  onValueChange={(value) => setDocForm({ ...docForm, doc_type: value })}
                >
                  <SelectTrigger className="input-default" data-testid="doc-type-select">
                    <SelectValue placeholder="Select document type" />
                  </SelectTrigger>
                  <SelectContent>
                    {documentTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-300">Status</Label>
                <Select 
                  value={docForm.status} 
                  onValueChange={(value) => setDocForm({ ...docForm, status: value })}
                >
                  <SelectTrigger className="input-default" data-testid="doc-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="complete">Complete</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="missing">Missing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-300">Notes (Optional)</Label>
                <Textarea
                  placeholder="Add any notes..."
                  value={docForm.notes}
                  onChange={(e) => setDocForm({ ...docForm, notes: e.target.value })}
                  className="input-default min-h-[80px]"
                  data-testid="doc-notes-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-300">Upload File (Optional)</Label>
                <div className="border-2 border-dashed border-slate-700 rounded-xl p-4 text-center hover:border-primary/50 transition-colors">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                    onChange={(e) => setDocForm({ ...docForm, file: e.target.files[0] })}
                    className="hidden"
                    id="doc-file-input"
                    data-testid="doc-file-input"
                  />
                  <label htmlFor="doc-file-input" className="cursor-pointer">
                    {docForm.file ? (
                      <div className="flex items-center justify-center gap-2 text-primary">
                        <FileText className="w-5 h-5" />
                        <span className="text-sm font-medium">{docForm.file.name}</span>
                        <span className="text-xs text-slate-500">({(docForm.file.size / 1024).toFixed(0)} KB)</span>
                      </div>
                    ) : (
                      <div>
                        <Upload className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                        <p className="text-slate-400 text-sm">Click to upload a file</p>
                        <p className="text-slate-500 text-xs mt-1">PDF, JPG, PNG, DOC, XLS (max 10MB)</p>
                      </div>
                    )}
                  </label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDocModal(false)} className="btn-secondary">
                Cancel
              </Button>
              <Button onClick={handleAddDocument} className="btn-primary" disabled={uploadingFile} data-testid="save-document-btn">
                {uploadingFile ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>
                ) : "Add Document"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Lease Modal */}
        <Dialog open={showLeaseModal} onOpenChange={setShowLeaseModal}>
          <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">Add Lease</DialogTitle>
              <DialogDescription className="text-slate-400">
                Record a new lease for this asset
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Tenant Name</Label>
                <Input
                  placeholder="John Doe"
                  value={leaseForm.tenant_name}
                  onChange={(e) => setLeaseForm({ ...leaseForm, tenant_name: e.target.value })}
                  className="input-default"
                  data-testid="tenant-name-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Email</Label>
                  <Input
                    type="email"
                    placeholder="john@example.com"
                    value={leaseForm.tenant_email}
                    onChange={(e) => setLeaseForm({ ...leaseForm, tenant_email: e.target.value })}
                    className="input-default"
                    data-testid="tenant-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Phone</Label>
                  <Input
                    placeholder="+234..."
                    value={leaseForm.tenant_phone}
                    onChange={(e) => setLeaseForm({ ...leaseForm, tenant_phone: e.target.value })}
                    className="input-default"
                    data-testid="tenant-phone-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full input-default justify-start" data-testid="start-date-picker">
                        <CalendarIcon className="w-4 h-4 mr-2 text-slate-500" />
                        {leaseForm.start_date ? format(leaseForm.start_date, "PP") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-800">
                      <Calendar
                        mode="single"
                        selected={leaseForm.start_date}
                        onSelect={(date) => setLeaseForm({ ...leaseForm, start_date: date })}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full input-default justify-start" data-testid="end-date-picker">
                        <CalendarIcon className="w-4 h-4 mr-2 text-slate-500" />
                        {leaseForm.end_date ? format(leaseForm.end_date, "PP") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-800">
                      <Calendar
                        mode="single"
                        selected={leaseForm.end_date}
                        onSelect={(date) => setLeaseForm({ ...leaseForm, end_date: date })}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Rent Amount (₦)</Label>
                  <Input
                    type="number"
                    placeholder="500000"
                    value={leaseForm.rent_amount}
                    onChange={(e) => setLeaseForm({ ...leaseForm, rent_amount: e.target.value })}
                    className="input-default"
                    data-testid="rent-amount-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Payment Frequency</Label>
                  <Select 
                    value={leaseForm.payment_frequency} 
                    onValueChange={(value) => setLeaseForm({ ...leaseForm, payment_frequency: value })}
                  >
                    <SelectTrigger className="input-default" data-testid="payment-frequency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="annually">Annually</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLeaseModal(false)} className="btn-secondary">
                Cancel
              </Button>
              <Button onClick={handleAddLease} className="btn-primary" data-testid="save-lease-btn">
                Create Lease
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Asset Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="bg-slate-900 border-slate-800 max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Edit Asset</DialogTitle>
              <DialogDescription className="text-slate-400">Update asset details</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Asset Name</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="input-default"
                  data-testid="edit-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Asset Type</Label>
                  <Select value={editForm.asset_type} onValueChange={(val) => setEditForm({ ...editForm, asset_type: val })}>
                    <SelectTrigger className="input-default" data-testid="edit-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="land">Land</SelectItem>
                      <SelectItem value="residential">Residential</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Ownership</Label>
                  <Select value={editForm.ownership_type} onValueChange={(val) => setEditForm({ ...editForm, ownership_type: val })}>
                    <SelectTrigger className="input-default" data-testid="edit-ownership-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Individual</SelectItem>
                      <SelectItem value="corporate">Corporate</SelectItem>
                      <SelectItem value="estate">Estate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">State</Label>
                  <Input
                    value={editForm.state}
                    onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                    className="input-default"
                    data-testid="edit-state-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">LGA</Label>
                  <Input
                    value={editForm.lga}
                    onChange={(e) => setEditForm({ ...editForm, lga: e.target.value })}
                    className="input-default"
                    data-testid="edit-lga-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Address</Label>
                <Input
                  value={editForm.address}
                  onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                  className="input-default"
                  data-testid="edit-address-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Estimated Value (₦)</Label>
                <Input
                  type="number"
                  value={editForm.estimated_value}
                  onChange={(e) => setEditForm({ ...editForm, estimated_value: e.target.value })}
                  className="input-default"
                  data-testid="edit-value-input"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Plot / AGIS No.</Label>
                  <Input
                    value={editForm.plot_number}
                    onChange={(e) => setEditForm({ ...editForm, plot_number: e.target.value })}
                    placeholder="AGIS/FCT/001"
                    className="input-default"
                    data-testid="edit-plot-number-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Unit No.</Label>
                  <Input
                    value={editForm.unit_number}
                    onChange={(e) => setEditForm({ ...editForm, unit_number: e.target.value })}
                    placeholder="UNIT-A12"
                    className="input-default"
                    data-testid="edit-unit-number-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Block No.</Label>
                  <Input
                    value={editForm.block_number}
                    onChange={(e) => setEditForm({ ...editForm, block_number: e.target.value })}
                    placeholder="BLOCK-7"
                    className="input-default"
                    data-testid="edit-block-number-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Description</Label>
                <Textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="input-default min-h-[80px]"
                  data-testid="edit-description-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditModal(false)} className="btn-secondary">
                Cancel
              </Button>
              <Button onClick={handleUpdateAsset} className="btn-primary" data-testid="save-edit-btn">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Asset Confirmation */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent className="bg-slate-900 border-slate-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Delete Asset</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                Are you sure you want to delete "<strong>{asset?.name}</strong>"? This will permanently remove the asset along with all its documents and leases. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteAsset}
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={deleting}
                data-testid="confirm-delete-asset-btn"
              >
                {deleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting...</> : "Delete Asset"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Edit Document Modal */}
        <Dialog open={showEditDocModal} onOpenChange={setShowEditDocModal}>
          <DialogContent className="bg-slate-900 border-slate-800">
            <DialogHeader>
              <DialogTitle className="text-white">Edit Document</DialogTitle>
              <DialogDescription className="text-slate-400">Update document details</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Document Type</Label>
                <Select value={editDocForm.doc_type} onValueChange={(val) => setEditDocForm({ ...editDocForm, doc_type: val })}>
                  <SelectTrigger className="input-default" data-testid="edit-doc-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {documentTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Status</Label>
                <Select value={editDocForm.status} onValueChange={(val) => setEditDocForm({ ...editDocForm, status: val })}>
                  <SelectTrigger className="input-default" data-testid="edit-doc-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="complete">Complete</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="missing">Missing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Notes</Label>
                <Textarea
                  value={editDocForm.notes}
                  onChange={(e) => setEditDocForm({ ...editDocForm, notes: e.target.value })}
                  className="input-default min-h-[80px]"
                  data-testid="edit-doc-notes-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Replace File (Optional)</Label>
                <div className="border-2 border-dashed border-slate-700 rounded-xl p-4 text-center hover:border-primary/50 transition-colors">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                    onChange={(e) => setEditDocForm({ ...editDocForm, file: e.target.files[0] })}
                    className="hidden"
                    id="edit-doc-file-input"
                    data-testid="edit-doc-file-input"
                  />
                  <label htmlFor="edit-doc-file-input" className="cursor-pointer">
                    {editDocForm.file ? (
                      <div className="flex items-center justify-center gap-2 text-primary">
                        <FileText className="w-5 h-5" />
                        <span className="text-sm font-medium">{editDocForm.file.name}</span>
                      </div>
                    ) : (
                      <div>
                        <Upload className="w-6 h-6 text-slate-500 mx-auto mb-1" />
                        <p className="text-slate-400 text-sm">Click to replace file</p>
                      </div>
                    )}
                  </label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDocModal(false)} className="btn-secondary">Cancel</Button>
              <Button onClick={handleEditDocument} className="btn-primary" disabled={uploadingFile} data-testid="save-edit-doc-btn">
                {uploadingFile ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</> : "Save Changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Lease Modal */}
        <Dialog open={showEditLeaseModal} onOpenChange={setShowEditLeaseModal}>
          <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">Edit Lease</DialogTitle>
              <DialogDescription className="text-slate-400">Update lease details</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Tenant Name</Label>
                <Input value={editLeaseForm.tenant_name} onChange={(e) => setEditLeaseForm({ ...editLeaseForm, tenant_name: e.target.value })} className="input-default" data-testid="edit-lease-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Email</Label>
                  <Input type="email" value={editLeaseForm.tenant_email} onChange={(e) => setEditLeaseForm({ ...editLeaseForm, tenant_email: e.target.value })} className="input-default" data-testid="edit-lease-email-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Phone</Label>
                  <Input value={editLeaseForm.tenant_phone} onChange={(e) => setEditLeaseForm({ ...editLeaseForm, tenant_phone: e.target.value })} className="input-default" data-testid="edit-lease-phone-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full input-default justify-start" data-testid="edit-lease-start-date">
                        <CalendarIcon className="w-4 h-4 mr-2 text-slate-500" />
                        {editLeaseForm.start_date ? format(editLeaseForm.start_date, "PP") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-800">
                      <Calendar mode="single" selected={editLeaseForm.start_date} onSelect={(date) => setEditLeaseForm({ ...editLeaseForm, start_date: date })} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full input-default justify-start" data-testid="edit-lease-end-date">
                        <CalendarIcon className="w-4 h-4 mr-2 text-slate-500" />
                        {editLeaseForm.end_date ? format(editLeaseForm.end_date, "PP") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-800">
                      <Calendar mode="single" selected={editLeaseForm.end_date} onSelect={(date) => setEditLeaseForm({ ...editLeaseForm, end_date: date })} />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Rent (₦)</Label>
                  <Input type="number" value={editLeaseForm.rent_amount} onChange={(e) => setEditLeaseForm({ ...editLeaseForm, rent_amount: e.target.value })} className="input-default" data-testid="edit-lease-rent-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Frequency</Label>
                  <Select value={editLeaseForm.payment_frequency} onValueChange={(val) => setEditLeaseForm({ ...editLeaseForm, payment_frequency: val })}>
                    <SelectTrigger className="input-default" data-testid="edit-lease-frequency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="annually">Annually</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Status</Label>
                  <Select value={editLeaseForm.status} onValueChange={(val) => setEditLeaseForm({ ...editLeaseForm, status: val })}>
                    <SelectTrigger className="input-default" data-testid="edit-lease-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                      <SelectItem value="terminated">Terminated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditLeaseModal(false)} className="btn-secondary">Cancel</Button>
              <Button onClick={handleEditLease} className="btn-primary" data-testid="save-edit-lease-btn">Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AssetDetailPage;
